import {
  clearAllAuthData,
  setAuthData,
  getRefreshToken,
  getAuthHeader,
  getUserFromStorage,
} from "./auth-storage";
import { apiClient } from "@/lib/api/client";
import { LoginResponse, getCurrentUser } from "@/lib/api/auth";
import { AuthErrorType } from "@/lib/types/auth";
import { useAuthStore } from "@/store/auth-store";

// Single flight pattern - prevents concurrent refresh attempts
let singleRefreshPromise: Promise<void> | null = null;
let lastUserDataRefresh = 0;
const USER_DATA_REFRESH_COOLDOWN = 60000; // 1 minute

// Helper: Check if URL should skip token refresh
const shouldSkipRefresh = (url: string): boolean => {
  const skipPatterns = ["/auth/", "refresh", "login", "logout"];
  return skipPatterns.some((pattern) => url.includes(pattern));
};

// Helper: Check if URL should skip user data refresh
const shouldSkipUserDataRefresh = (url: string): boolean => {
  const skipPatterns = ["/auth/", "refresh", "login", "logout", "/users/me/"];
  return skipPatterns.some((pattern) => url.includes(pattern));
};

// Optional user data refresh before API requests
const refreshUserDataIfNeeded = async (): Promise<void> => {
  const now = Date.now();
  
  // Check if enough time has passed since last refresh
  if (now - lastUserDataRefresh < USER_DATA_REFRESH_COOLDOWN) {
    return;
  }

  try {
    const freshUser = await getCurrentUser();
    const currentUser = getUserFromStorage();
    
    // Only update if user data has actually changed
    if (currentUser && JSON.stringify(currentUser) !== JSON.stringify(freshUser)) {
      useAuthStore.setState({ user: freshUser });
      // Update storage
      const { updateUserData } = await import("./auth-storage");
      updateUserData(freshUser);
    }
    
    lastUserDataRefresh = now;
  } catch (error) {
    // Silently fail - don't interrupt the main request
    console.warn("Failed to refresh user data before API request:", error);
  }
};

export const refreshAuthToken = async (
  refresh: string
): Promise<LoginResponse> => {
  const user = getUserFromStorage();

  if (!user) throw new Error("User is not authenticated!");

  const response = await apiClient.post("dj-rest-auth/token/refresh/", {
    refresh,
  });

  // const { user, access, refresh: newRefreshToken } = response.data;
  const { access } = response.data;

  if (!access) {
    throw new Error("Invalid response format from refresh endpoint");
  }

  // Store the new tokens
  setAuthData(user, access, refresh);

  return {
    user,
    access,
    refresh,
  };
};

// Core token refresh logic with state management
const performTokenRefresh = async (): Promise<void> => {
  if (!singleRefreshPromise) {
    // Do not set auth store `isLoading` here. ProtectedRoute treats `isLoading` as
    // "initial auth check" and renders a full-page Loader, which unmounts the
    // entire app subtree (e.g. Stock In modal + form) during silent refresh.
    singleRefreshPromise = (async () => {
      try {
        const currentRefreshToken = getRefreshToken();
        if (!currentRefreshToken) {
          clearAllAuthData();
          throw new Error("No refresh token available");
        }

        // Refresh the token
        const { user } = await refreshAuthToken(currentRefreshToken);

        useAuthStore.setState({
          isAuthenticated: !!user,
          user,
          error: null,
        });
      } catch (refreshError) {
        // Handle refresh failure
        clearAllAuthData();
        useAuthStore.setState({
          isLoading: false,
          isAuthenticated: false,
          user: null,
        });
        throw refreshError;
      } finally {
        singleRefreshPromise = null;
      }
    })();
  }

  await singleRefreshPromise;
};

export const setupAuthInterceptors = () => {
  // Request interceptor - add auth header to outgoing requests
  apiClient.interceptors.request.use(
    async (config) => {
      const authHeader = getAuthHeader();
      if (authHeader && !config.headers.Authorization) {
        config.headers.Authorization = authHeader;
      }

      // Optionally refresh user data before API requests (except for auth endpoints)
      if (authHeader && !shouldSkipUserDataRefresh(config.url || '')) {
        // Don't await - let it run in background to avoid slowing down requests
        refreshUserDataIfNeeded().catch(() => {
          // Silently handle errors
        });
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401s and token refresh
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Skip refresh for auth-related endpoints to prevent infinite loops
        if (shouldSkipRefresh(originalRequest.url)) {
          clearAllAuthData();

          useAuthStore.setState({
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });

          error.type = AuthErrorType.EXPIRED_SESSION;
          return Promise.reject(error);
        }

        // Mark as retry to prevent infinite loops
        originalRequest._retry = true;

        try {
          await performTokenRefresh();

          // Retry original request with new token
          const newAuthHeader = getAuthHeader();
          if (newAuthHeader) {
            originalRequest.headers.Authorization = newAuthHeader;
            return apiClient(originalRequest);
          } else {
            // No auth header after refresh - something went wrong
            clearAllAuthData();
            useAuthStore.setState({
              isLoading: false,
              isAuthenticated: false,
              user: null,
            });
            return Promise.reject(error);
          }
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // Set error type for other cases
      error.type =
        error.response?.status === 401
          ? AuthErrorType.EXPIRED_SESSION
          : AuthErrorType.UNKNOWN;

      return Promise.reject(error);
    }
  );
};
